import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { HiOutlineEye } from "react-icons/hi";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { PiInvoiceBold } from "react-icons/pi";
import { BsBuilding } from "react-icons/bs";
import { TbDeviceLandlinePhone } from "react-icons/tb";
import { Link } from "react-router-dom";
import { ActionIcon, Tooltip } from "rizzui";
import { toast } from "sonner";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { menuRoutes } from "../../../router/menu";
import { ProviderService } from "../../../services/providers/providerService";
import { Provider, ProviderApiResponse } from "../../../type/entities/provider-type";
import { providerStatuses, retributionTypes } from "../../../utils/SelectorMapping";
import ProviderForm from "../components/ProviderForm";
import ProvidersFilters from "./ProvidersFilters";
import useHandleErrors from "../../../hooks/useHandleErrors";
import ProviderDeleteAllModal from "../components/ProviderDeleteAllModal";
import DeletePopover from "../../../components/buttons/DeletePopover";

interface ProvidersListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    providerSelected: Provider | null;
    setProviderSelected: React.Dispatch<React.SetStateAction<Provider | null>>;
    openDeleteAllModal: boolean;
    setOpenDeleteAllModal: React.Dispatch<React.SetStateAction<boolean>>;
    registerTableRefetch?: (refetch: () => void) => void;
}

const columnOrder = [
    'id',
    'company',
    'companyName',
    'headquartersCount',
    'code',
    'commercialName',
    'activity',
    'website',
    'telephone',
    'telephone2',
    'retributionType',
    'providerType',
    'autoInvoice',
    'cif',
    'cifIsReal',
    'comments',
    'currentInvoiceNumber',
    'email',
    'invoiceControlNumber',
    'lastInvoiceDate',
    'taxDomicile',
    'status',
    'createdAt',
    'updatedAt'
];

const ProvidersList: FC<ProvidersListProps> = ({ openFormDrawer, setOpenFormDrawer, providerSelected, setProviderSelected, openDeleteAllModal, setOpenDeleteAllModal, registerTableRefetch }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new ProviderService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE PROVEEDORES
     * @EN GETS THE LIST OF PROVIDERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listProviders(filters);
        return response.getResponseData() as ProviderApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DEL PROVEEDOR
     * @EN TOGGLE PROVIDER STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleStatusChange = async (id: string, status: string) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleProviderStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(response.message);
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


        //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIOS PROVEEDORES
     * @EN HANDLES THE DELETION OF MULTIPLE PROVIDERS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiProvider(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Proveedores eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE UN PROVEEDOR
     * @EN HANDLES THE DELETION OF A PROVIDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteProvider(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Proveedor eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    console.log(data);
    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'providers-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isSortable
                    customRowsBackgroundColors={[
                        {
                            column: 'cifIsReal',
                            values: [false as any, 0 as any, '0' as any],
                            bgColor: 'bg-amber-50/60 dark:bg-amber-950/20',
                        }
                    ]}
                    children={
                        <>
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                                <span className="h-3 w-3 rounded-sm border border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/20" />
                                <span>Proveedor con CIF ficticio</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                                <span className="h-3 w-3 rounded-sm border border-gray-200 bg-gray-50/60 dark:border-gray-900/40 dark:bg-gray-950/20" />
                                <span>Proveedor con CIF real</span>
                            </div>
                        </>
                    }
                    columnsNoSortables={['status', 'headquartersCount']}
                    columnsNotShown={[
                        'autoInvoice',
                        'email',
                        'cif',
                        'cifIsReal',
                        'comments',
                        'invoiceControlNumber',
                        'lastInvoiceDate',
                        'code',
                        'commercialName',
                        'activity',
                        'website',
                        'telephone2',
                    ]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'companyName',
                            columnNameKey: 'Nombre de la empresa',
                            render: (row: any) => {
                                let comments = row.row.original?.comments || 'Sin Comentarios';
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={<div className="max-w-[350px] text-ellipsis text-wrap">{comments}</div>}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div className="flex flex-col">
                                            <div className="font-bold">{row.row.original.companyName}</div>
                                            <div className="text-gray-500 text-nowrap"><span className="font-bold">Nombre comercial: </span>{row.row.original?.commercialName ?? 'N/A'}</div>
                                            <div className="text-gray-500 text-nowrap"><span className="font-bold">Email: </span>{row.row.original?.email ?? 'N/A'}</div>
                                            <div className="text-gray-500 text-nowrap"><span className="font-bold">CIF: </span>{row.row.original?.cif ?? 'N/A'}</div>
                                            <div className="text-gray-500 text-nowrap"><span className="font-bold">Domicilio fiscal: </span>{row.row.original?.taxDomicile ?? 'N/A'}</div>
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'taxDomicile',
                            columnNameKey: 'Domicilio fiscal',
                            render: (row: any) => {
                                return (
                                    <div className="text-ellipsis text-wrap max-w-[200px]">
                                        {row.row.original?.taxDomicile ?? 'N/A'}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'headquartersCount',
                            columnNameKey: 'Sedes',
                            size: 90,
                            render: (row: any) => {
                                const count = row.row.original?.headquartersCount ?? 0;
                                return (
                                    <div className="flex items-center justify-center">
                                        <BsBuilding className="mr-2 h-4 w-4" />
                                        {count}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'providerType',
                            columnNameKey: 'Tipo de proveedor',
                            render: (row: any) => {
                                return (
                                    <span className="flex flex-nowrap gap-2 capitalize">
                                        {row.row.original?.providerType?.name ?? 'N/A'}
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'retributionType',
                            columnNameKey: 'Tipo de retribución',
                            render: (row: any) => {
                                let retributionType = retributionTypes.find((item) => item.value == row.row.original?.retributionType)?.label;
                                return (
                                    <div className="flex flex-nowrap gap-2 capitalize items-center">
                                        <PiInvoiceBold className="text-gray-400 min-h-[15px] min-w-[15px]" />
                                        {retributionType == undefined || retributionType == '' || retributionType == null ? 'N/A' : retributionType}
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'telephone',
                            columnNameKey: 'Teléfonos',
                            size: 200,
                            render: (row: any) => {
                                const formatPhone = (value: unknown) => {
                                    if (value === undefined || value === null || value === '') return 'N/A';
                                    const singleLine = String(value).replace(/\r?\n|\r/g, ' ').trim();
                                    return singleLine === '' ? 'N/A' : singleLine;
                                };
                                const telephone = formatPhone(row.row.original?.telephone);
                                const telephone2 = formatPhone(row.row.original?.telephone2);
                                return (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex flex-row flex-nowrap items-center gap-2">
                                            <TbDeviceLandlinePhone className="shrink-0 text-gray-400 min-h-[15px] min-w-[15px]" />
                                            <span className="whitespace-nowrap text-nowrap">{telephone}</span>
                                        </div>
                                        <div className="flex flex-row flex-nowrap items-center gap-2">
                                            <TbDeviceLandlinePhone className="shrink-0 text-gray-400 min-h-[15px] min-w-[15px]" />
                                            <span className="whitespace-nowrap text-nowrap">{telephone2}</span>
                                        </div>
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'status',
                            columnNameKey: 'Estado',
                            render: (row: any) => {
                                const status = row.row.original.status;
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id + status?.id}>
                                        <StatusDropdown
                                            entityId={row.row.original.id}
                                            title="¿Estás seguro de cambiar el estado del proveedor?"
                                            statesOptions={providerStatuses}
                                            currentState={{
                                                name: capitalize(status?.name),
                                                id: status?.id,
                                                color: status?.color
                                            }}
                                            handleStateChange={(entityId: string, newStateId: string) => handleStatusChange(entityId, newStateId)}
                                            disabled={changingStatus.includes(row.row.original.id)}
                                            hasComments={false}
                                        />
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'currentInvoiceNumber',
                            columnNameKey: 'Facturación',
                            render: (row: any) => {
                                const date = row.row.original?.lastInvoiceDate?.date;

                                return (
                                    <div className="flex flex-col">
                                        <div className="font-bold">Nº  {row.row.original?.currentInvoiceNumber ?? 'N/A'}</div>
                                        <div className="text-gray-500 text-nowrap"><span className="font-bold">Nº Control: </span>{row.row.original?.invoiceControlNumber ?? 'N/A'}</div>
                                        <div className="text-gray-500 text-nowrap"><span className="font-bold">Última Factura: </span>{date ? moment(date).format('DD/MM/YYYY') : 'N/A'}</div>
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'provider', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.providers.path}/${row.id}/profile/${menuRoutes.providers.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white h-6 w-6"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'provider', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setProviderSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white h-6 w-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'provider', permission: 'delete' },
                            render: (row: any) => {
                                const headquartersCount = row.headquartersCount ?? 0;
                                const warningText = headquartersCount > 0
                                    ? `Este proveedor tiene ${headquartersCount} sede(s) asociada(s). Si lo eliminas, también se eliminarán sus sedes.`
                                    : null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar proveedor`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.companyName}?`}
                                                warningText={warningText}
                                                onDelete={() => handleDelete(row.id)}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ProviderApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Proveedores'} onClear={() => resetFilters(50)}>
                <ProvidersFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    providerSelected ?
                        "Completa el formulario para editar los datos del proveedor" :
                        "Completa el formulario para crear un nuevo proveedor"
                }
                drawerTitle={providerSelected ? 'Editar Proveedor' : 'Crear Proveedor'} submitText={providerSelected ? 'Guardar Proveedor' : 'Crear Proveedor'}>
                <ProviderForm setOpenModal={setOpenFormDrawer} provider={providerSelected} refetch={refetch} />
            </FormDrawerView>

            <ProviderDeleteAllModal isOpen={openDeleteAllModal} onClose={() => setOpenDeleteAllModal(false)} refetch={refetch} />

        </Page>
    );
};

export default ProvidersList;
